/**
 * Newsletter UX helper
 *
 * UI/UX mode only:
 * - validates input for the prototype
 * - simulates async submit feedback
 * - keeps a lightweight local record so repeated signups can show a realistic state
 */
export interface NewsletterSubmissionInput {
  consent: boolean;
  email: string;
  favoritesCount?: number;
  honeypot?: string;
  pathname: string;
  source: string;
  tags?: string[];
}

export interface NewsletterSubmissionResult {
  ok: true;
  status: 'subscribed' | 'already_subscribed';
  storage?: string;
}

interface NewsletterSubmissionPayload extends NewsletterSubmissionInput {
  pageUrl: string;
  utm: {
    campaign?: string;
    content?: string;
    medium?: string;
    source?: string;
    term?: string;
  };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UX_STORAGE_KEY = 'sunmarbella_newsletter_demo_signups';

export function isValidNewsletterEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

function getUtmContext(): NewsletterSubmissionPayload['utm'] {
  const params = new URLSearchParams(window.location.search);

  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    content: params.get('utm_content') || undefined,
    term: params.get('utm_term') || undefined,
  };
}

function buildPayload(input: NewsletterSubmissionInput): NewsletterSubmissionPayload {
  return {
    ...input,
    email: input.email.trim().toLowerCase(),
    pageUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    utm: getUtmContext(),
  };
}

function readStoredEmails(): string[] {
  try {
    const raw = window.localStorage.getItem(UX_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeStoredEmails(emails: string[]): void {
  window.localStorage.setItem(UX_STORAGE_KEY, JSON.stringify(emails));
}

export async function submitNewsletterLead(
  input: NewsletterSubmissionInput
): Promise<NewsletterSubmissionResult> {
  // Silently accept but discard bot submissions that fill the honeypot
  if (input.honeypot) {
    await new Promise((resolve) => window.setTimeout(resolve, 700));
    return { ok: true, status: 'subscribed', storage: 'honeypot_rejected' };
  }

  const payload = buildPayload(input);
  const knownEmails = readStoredEmails();
  const isExisting = knownEmails.includes(payload.email);

  await new Promise((resolve) => window.setTimeout(resolve, 700));

  if (!isExisting) {
    writeStoredEmails([...knownEmails, payload.email]);
  }

  return {
    ok: true,
    status: isExisting ? 'already_subscribed' : 'subscribed',
    storage: 'ux_demo_local',
  };
}
